// Guards de rota declarativos.
// Hierarquia de roles (readme §10): GESTOR > GERENTE > VENDEDOR.
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { ROLE_RANK, type Role } from '@/config/constants';
import {
  selectCurrentUser,
  selectIsAuthed,
  selectOnboardingComplete,
  selectRole,
} from '@/features/auth';
import { landingRouteForRole, ROUTES } from './paths';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthed = useAppSelector(selectIsAuthed);
  const location = useLocation();
  if (!isAuthed) {
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

interface RequireGuestProps {
  children: ReactNode;
}

/** Inverso de RequireAuth — redireciona quem já está logado pra landing do papel. */
export function RequireGuest({ children }: RequireGuestProps) {
  const isAuthed = useAppSelector(selectIsAuthed);
  const role = useAppSelector(selectRole);
  if (isAuthed) return <Navigate to={landingRouteForRole(role)} replace />;
  return <>{children}</>;
}

/**
 * Manda o usuário pra landing do papel (RF-01). Usado no catch-all e no index —
 * onde "a home" depende de quem está logado. Quando a landing É a raiz, renderiza
 * `children` (o Dashboard) em vez de redirecionar pra si mesmo (loop).
 */
export function LandingRedirect({ children }: { children?: ReactNode }) {
  const role = useAppSelector(selectRole);
  const target = landingRouteForRole(role);
  if (children && target === ROUTES.root) return <>{children}</>;
  return <Navigate to={target} replace />;
}

interface RequireRoleProps {
  /** role mínima na hierarquia GESTOR > GERENTE > VENDEDOR */
  min?: Role;
  /** lista explícita de roles permitidas (qualquer das listadas passa) */
  oneOf?: Role[];
  children: ReactNode;
}

export function RequireRole({ min, oneOf, children }: RequireRoleProps) {
  const user = useAppSelector(selectCurrentUser);
  if (!user) return <Navigate to={ROUTES.login} replace />;
  if (oneOf && !oneOf.includes(user.role)) {
    return <ForbiddenView />;
  }
  if (min && ROLE_RANK[user.role] < ROLE_RANK[min]) {
    return <ForbiddenView />;
  }
  return <>{children}</>;
}

/** Mantido por compatibilidade — preferir RequireRole. */
export function RequireMinRole({ role, children }: { role: Role; children: ReactNode }) {
  return <RequireRole min={role}>{children}</RequireRole>;
}

interface RequireOnboardingProps {
  children: ReactNode;
  /** Se true, exige onboarding INCOMPLETO (rota /onboarding). Default false (= exige completo). */
  incomplete?: boolean;
}

export function RequireOnboarding({ children, incomplete = false }: RequireOnboardingProps) {
  const complete = useAppSelector(selectOnboardingComplete);
  const user = useAppSelector(selectCurrentUser);
  // Onboarding só é relevante pra GESTOR. Outros papéis nunca caem nessa página.
  if (user && user.role !== 'GESTOR') {
    return incomplete ? <Navigate to={ROUTES.root} replace /> : <>{children}</>;
  }
  if (incomplete && complete) return <Navigate to={ROUTES.root} replace />;
  if (!incomplete && !complete) return <Navigate to={ROUTES.onboarding} replace />;
  return <>{children}</>;
}

function ForbiddenView() {
  return (
    <main style={{ padding: 'var(--spacing-2xl)', textAlign: 'center' }} role="alert">
      <h1>Acesso negado</h1>
      <p>Você não tem permissão para acessar essa página.</p>
    </main>
  );
}
